<script setup lang="ts">
import { inject, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { useAssignmentsStore } from '@/stores/assignments'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import { useSubjectsStore } from '@/stores/subjects'
import { useFormsStore } from '@/stores/forms'
import { useSyncStore } from '@/stores/sync'
import FormShell from '@/components/forms/FormShell.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'

const route = useRoute()
const opsStore = useOpsStore()
const assignmentsStore = useAssignmentsStore()
const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()
const subjectsStore = useSubjectsStore()
const formsStore = useFormsStore()
const syncStore = useSyncStore()
const incidentId = inject<string>('incidentId', '')
const assignmentId = route.params.assignmentId as string

const formKey = computed(() => `SAR104S_${assignmentId}`)
const formData = computed(() => formsStore.getFormData(formKey.value))

// Live derived values from stores
const assignment = computed(() =>
  assignmentsStore.assignments.find((a) => a.assignment_id === assignmentId),
)
const teamAssignment = computed(() =>
  assignmentsStore.teamAssignments.find((ta) => ta.assignment_id === assignmentId),
)
const team = computed(() => {
  const taTeamId = teamAssignment.value?.team_id
  return taTeamId ? teamsStore.teams.find((t) => t.team_id === taTeamId) : undefined
})
const teamMembers = computed(() => {
  const taTeamId = teamAssignment.value?.team_id
  if (!taTeamId) return []
  return personnelStore.roster.filter((p) => p.team_id === taTeamId && !p.time_out)
})
const teamMembersText = computed(() =>
  teamMembers.value.map((p) => `${p.first} ${p.last}`).join(', '),
)
const primarySubject = computed(() => subjectsStore.subjects[0])
const subjectLkp = computed(() => primarySubject.value?.lkp_description ?? '')
const subjectCoords = computed(() => {
  const s = primarySubject.value
  if (s?.lkp_lat && s?.lkp_lng) return `${s.lkp_lat.toFixed(5)}, ${s.lkp_lng.toFixed(5)}`
  return ''
})

// Pre-populate form fields on first open
watch(
  assignment,
  async (a) => {
    if (!a || !incidentId) return
    const existing = formsStore.getFormData(formKey.value)
    if (existing['_initialized']) return
    await Promise.all([
      formsStore.saveFormField(incidentId, formKey.value, '_initialized', true),
      formsStore.saveFormField(incidentId, formKey.value, 'situation_narrative', a.description ?? ''),
      formsStore.saveFormField(incidentId, formKey.value, 'prev_search_efforts', a.prev_search_efforts ?? ''),
    ])
  },
  { immediate: true },
)

function field(key: string): string {
  const v = formData.value[key]
  return v != null ? String(v) : ''
}

async function save(fieldPath: string, event: Event) {
  if (!incidentId) return
  const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value
  syncStore.pendingWrites++
  try {
    await formsStore.saveFormField(incidentId, formKey.value, fieldPath, value)
  } finally {
    syncStore.pendingWrites--
  }
}
</script>

<template>
  <FormShell
    :form-type="formKey"
    title="SAR 104S · Team Assignment"
    back-route="incident-form-sar104s-list"
    back-label="Team Assignments"
    :back-params="{ id: incidentId }"
  >
    <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
      <!-- Section head -->
      <div class="flex items-end gap-3.5 flex-wrap">
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">SAR 104S · Team Assignment</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">
            {{ assignment?.assignment_name ?? 'Team Assignment' }}
          </h2>
        </div>
        <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Assignment briefing, search instructions, and safety information for the assigned team.</p>
      </div>

      <!-- Assignment Information (read-only, from stores) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Assignment Information</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Read-only</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Assignment Name</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ assignment?.assignment_name ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Team Identifier</label>
            <p class="font-mono text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ team?.team_identifier ?? '—' }}</p>
          </div>
          <div class="col-span-2">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Team Members</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded min-h-[38px]">{{ teamMembersText || '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Subject Last Known Point</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ subjectLkp || '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">LKP Coordinates</label>
            <p class="font-mono text-[12px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ subjectCoords || '—' }}</p>
          </div>
        </div>
      </div>

      <!-- Situation Narrative -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Situation Narrative</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('situation_narrative')"
            placeholder="Current situation description for this assignment…"
            :rows="4"
            @blur="save('situation_narrative', $event)"
          />
        </div>
      </div>

      <!-- Previous Search Efforts -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Previous Search Efforts</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('prev_search_efforts')"
            placeholder="Summary of previous search activity in this area…"
            :rows="3"
            @blur="save('prev_search_efforts', $event)"
          />
        </div>
      </div>

      <!-- Search Instructions / Resources -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Search Instructions & Resources Needed</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('resources_needed')"
            placeholder="Specific search instructions, tactics, and any additional resources required…"
            :rows="4"
            @blur="save('resources_needed', $event)"
          />
        </div>
      </div>

      <!-- Safety & Communications -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Safety & Communications</h3>
        </div>
        <div class="p-3.5 flex flex-col gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Safety Message</label>
            <FosTextarea
              :model-value="field('safety_message')"
              placeholder="Assignment-specific safety notes…"
              :rows="3"
              @blur="save('safety_message', $event)"
            />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Primary Radio Frequency</label>
              <FosInput
                :model-value="field('radio_frequency')"
                placeholder="e.g. 155.340 MHz"
                @blur="save('radio_frequency', $event)"
              />
            </div>
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Reporting Interval</label>
              <FosInput
                :model-value="field('reporting_interval')"
                placeholder="e.g. Every 30 minutes"
                @blur="save('reporting_interval', $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </FormShell>
</template>
